/**
 * Immutable Flow IR edit commands. Each returns a NEW FlowDefinition; the original node/edge objects
 * are SPREAD (never rebuilt from a whitelist) so unknown/forward-compat fields carry through
 * losslessly (see toIR.ts). These are the transaction-grouped "commands" the undo stack snapshots
 * (spec §5.3 — one user intention = one undo step).
 */
import type { LifecycleStage } from '@/theme/tokens';
import { edgeId, isControlEdge, type EdgeKind, type FlowDefinition, type FlowEdge, type FlowNode } from './types';

/** Nodes live only on base definitions; variants carry a delta (handled elsewhere). */
function nodes(def: FlowDefinition): FlowNode[] {
  return def.nodes ?? [];
}
function edges(def: FlowDefinition): FlowEdge[] {
  return def.edges ?? [];
}

/** A unique node id within the definition, seeded from a kind-ish prefix. */
export function freshNodeId(def: FlowDefinition, prefix: string): string {
  const existing = new Set(nodes(def).map((n) => n.id));
  const base = (prefix || 'node').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
  let i = 1;
  let id = `${base}-${i}`;
  while (existing.has(id)) {
    i += 1;
    id = `${base}-${i}`;
  }
  return id;
}

/** A unique explicit edge id (schema-valid; keeps canvas selection stable across status edits). */
export function freshEdgeId(def: FlowDefinition, e: FlowEdge): string {
  const existing = new Set(edges(def).map((x) => x.id ?? edgeId(x)));
  const base = edgeId(e);
  if (!existing.has(base)) return base;
  let i = 2;
  while (existing.has(`${base}~${i}`)) i += 1;
  return `${base}~${i}`;
}

export function addNode(def: FlowDefinition, node: FlowNode): FlowDefinition {
  return { ...def, nodes: [...nodes(def), node] };
}

export function updateNode(
  def: FlowDefinition,
  id: string,
  patch: Partial<FlowNode>,
): FlowDefinition {
  return {
    ...def,
    nodes: nodes(def).map((n) => (n.id === id ? { ...n, ...patch } : n)),
  };
}

/** Replace a node's whole config object (spread preserves the rest of the node incl. unknown fields). */
export function setNodeConfig(
  def: FlowDefinition,
  id: string,
  config: Record<string, unknown>,
): FlowDefinition {
  return updateNode(def, id, { config });
}

export function setNodeStage(def: FlowDefinition, id: string, stage: LifecycleStage): FlowDefinition {
  return updateNode(def, id, { stage });
}

/** Delete a node and every edge that touches it (edge cleanup — interaction #10). */
export function deleteNode(def: FlowDefinition, id: string): FlowDefinition {
  return {
    ...def,
    nodes: nodes(def).filter((n) => n.id !== id),
    edges: edges(def).filter((e) => e.from !== id && e.to !== id),
  };
}

export function addEdge(def: FlowDefinition, edge: FlowEdge): FlowDefinition {
  return { ...def, edges: [...edges(def), edge] };
}

export function updateEdge(
  def: FlowDefinition,
  id: string,
  patch: Partial<FlowEdge>,
): FlowDefinition {
  return {
    ...def,
    edges: edges(def).map((e) => ((e.id ?? edgeId(e)) === id ? normalizeEdge({ ...e, ...patch }) : e)),
  };
}

export function deleteEdge(def: FlowDefinition, id: string): FlowDefinition {
  return { ...def, edges: edges(def).filter((e) => (e.id ?? edgeId(e)) !== id) };
}

/**
 * Enforce the 3-kind discipline structurally (spec §4.5; schema `edge.allOf`): a data edge carries a
 * binding and NEVER a status/guard (it cannot gate); a status edge carries onStatus; a sequence edge
 * may carry a guard only. Mirrors the server analyser so the UI never offers an unrepresentable edge.
 */
export function normalizeEdge(e: FlowEdge): FlowEdge {
  const base: FlowEdge = { id: e.id, from: e.from, to: e.to, kind: e.kind, obligations: e.obligations };
  if (e.kind === 'status') {
    base.onStatus = e.onStatus ?? '';
  } else if (e.kind === 'data') {
    base.binding = e.binding ?? { source: '', target: '' };
  } else {
    // sequence
    if (e.guard) base.guard = e.guard;
  }
  return base;
}

/** Build a schema-valid edge for a fresh connection of a given kind. */
export function makeEdge(
  def: FlowDefinition,
  from: string,
  to: string,
  kind: EdgeKind,
  extra?: { onStatus?: string; guard?: string; binding?: { source: string; target: string } },
): FlowEdge {
  const draft = normalizeEdge({ from, to, kind, ...extra });
  return { ...draft, id: freshEdgeId(def, draft) };
}

/** True when a data edge would (illegally) be asked to gate — used for defensive UI guards. */
export function edgeGates(e: FlowEdge): boolean {
  return isControlEdge(e);
}
